/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { classNames } from '../utils/UNSAFE_classNames';
import { Keys, containsKey } from '../utils/UNSAFE_keys';
import { TableColumn } from './Table';
import { TableFooterCell } from './TableFooterCell';
import { tableFooterRowStyles, tableFooterRowContainerStyles } from './themes/Table.css';

/**
 * Props for the TableFooter Component
 */
export type TableFooterProps<K, D, C> = {
  tableId: string;
  isRendered: boolean;
  columnsArray: TableColumn<K, D, C>[];
  hasVerticalGridlines: boolean;
  containsTabbableCell: boolean;
  focusedKey?: C;
  showFocusRing: boolean;
  isRtl: boolean;
  selectedKeys?: Keys<C>;
  isColumnSelectionEnabled: boolean;
  startStickyEdge?: number;
  endStickyEdge?: number;
};

/**
 * The internal component used to render a footer region in Table.
 */
export function TableFooter<K, D, C>({
  tableId,
  isRendered,
  columnsArray,
  hasVerticalGridlines,
  focusedKey,
  showFocusRing,
  selectedKeys,
  isColumnSelectionEnabled,
  containsTabbableCell,
  startStickyEdge,
  endStickyEdge,
  isRtl
}: TableFooterProps<K, D, C>) {
  const rowClasses = classNames([tableFooterRowStyles.base]);
  const containerClasses = classNames([tableFooterRowContainerStyles.base]);

  let isPreviousSelected = false;
  return isRendered ? (
    <tfoot class={containerClasses}>
      <tr class={rowClasses} role={'row'} data-oj-table-footer-row={tableId}>
        {columnsArray.map((column: TableColumn<K, D, C>, columnIndex: number) => {
          const columnKey = column.key;
          const isSelected = containsKey(selectedKeys, columnKey);
          const footerCellProps = {
            tableId: tableId,
            columnKey: columnKey,
            columnIndex: columnIndex,
            isFinalColumnIndex: columnIndex === columnsArray.length - 1,
            hasVerticalGridlines: hasVerticalGridlines,
            stickyColumn: column.value.stickyEdge,
            isSelected: isSelected,
            isPreviousSelected: isPreviousSelected,
            isColumnSelectable: isColumnSelectionEnabled && column.value.selectable !== 'disabled',
            isFocusRingVisible: showFocusRing && columnKey === focusedKey && !containsTabbableCell,
            isTabbable: columnKey === focusedKey && containsTabbableCell,
            footerText: column.value.footerText,
            renderer: column.value.footerRenderer,
            isStickyStartEdge: columnIndex === startStickyEdge,
            isStickyEndEdge: columnIndex === endStickyEdge,
            isRtl: isRtl
          };
          isPreviousSelected = isSelected;
          return <TableFooterCell {...footerCellProps} />;
        })}
      </tr>
    </tfoot>
  ) : null;
}
